import Request, { GET } from './Request'
import toInteger from 'lodash/toInteger'
import UnauthorizedError from './Errors/RequestError/UnauthorizedError'
import ForbiddenError from './Errors/RequestError/ForbiddenError'
import NotFoundError from './Errors/RequestError/NotFoundError'
import RequestError from './Errors/RequestError'
import MethodNotAllowedError from './Errors/RequestError/MethodNotAllowedError'
import Store from '@/store'
import { SET_CACHE } from '@/store/mutation-types'

export default class extends Array {
  constructor (resource) {
    super()

    Object.defineProperties(this, {
      page: {
        configurable: false,
        enumerable: false,
        writable: true
      },
      limit: {
        configurable: false,
        enumerable: false,
        writable: true
      },
      total: {
        configurable: false,
        enumerable: false,
        writable: true
      },
      reload: {
        configurable: false,
        enumerable: false,
        value: async function (callback) {
          this._request.page = this.page
          this._request.limit = this.limit

          if (typeof callback === 'function') {
            await Promise.resolve(callback(this._request))
          }

          const request = await this._request.build()

          let response = await fetch(request)

          this.splice(0, this.length)

          if (response.ok) {
            response = await response.json()

            response
              .data[Object.keys(response.data).shift()]
              .map(d => {
                /* eslint-disable new-cap */
                const r = new this._resource()
                r.fill(d, true)
                this.push(r)
              })

            if (typeof response.metas === 'object') {
              this.page = toInteger(response.metas.current_page)
              this.limit = toInteger(response.metas.per_page)
              this.total = toInteger(response.metas.total)
            }
          }
        },
        writable: true
      },
      _resource: {
        configurable: false,
        enumerable: false,
        value: resource,
        writable: false
      },
      _request: {
        configurable: false,
        enumerable: false,
        value: Object.assign(new Request(), {
          method: GET,
          page: 1,
          limit: 20
        }),
        writable: false
      }
    })
  }

  static async list (resource, callback, abortSignal = null) {
    const resources = new this(resource)

    resources._request.context = resource

    if (typeof callback === 'function') {
      await Promise.resolve(callback(resources._request))
    }

    const request = await resources._request.build()

    if (resource.name === 'filter') {
      const url = new URL(request.url)
      const baseResource = url.searchParams.get('resource')

      // Check if filters for the resource are cached before making a request.
      const cachedFilters = Store.getters['cache/pages']('filters_' + baseResource)

      if (cachedFilters) {
        console.log('Using cached filters for resource', baseResource)
        return cachedFilters
      }
    }

    let response = null

    if (abortSignal) {
      response = await fetch(request, { signal: abortSignal })
    } else {
      response = await fetch(request)
    }

    if (response.ok) {
      response = await response.json()

      response
        .data[Object.keys(response.data).shift()]
        .map(d => {
          const r = new resources._resource()
          r.fill(d, true)
          resources.push(r)
        })

      if (resource.name === 'filter') {
        const url = new URL(request.url)
        const baseResource = url.searchParams.get('resource')

        // Cache filters for use by the filter builder.
        Store.commit(`cache/${SET_CACHE}`, {
          type: 'pages',
          url: 'filters_' + baseResource,
          results: resources
        })
      }

      if (typeof response.metas === 'object') {
        resources.page = toInteger(response.metas.current_page)
        resources.limit = toInteger(response.metas.per_page)
        resources.total = toInteger(response.metas.total)
      }
    } else {
      switch (response.status) {
        case 400:
          const data = (await response.json()).data

          Object
            .keys(data)
            .map(field => {
              resource
                .$fields[field]
                .messages = data[field]
            })
          break
        case 401:
          throw new UnauthorizedError(resource, request, response, 'Unauthorized Error')
        case 403:
          throw new ForbiddenError(resource, request, response, 'Permission Denied Error')
        case 404:
          throw new NotFoundError(resource, request, response, 'Resource Not Found Error')
        case 405:
          throw new MethodNotAllowedError(resource, request, response, 'Request method is not allowed')
        default:
          throw new RequestError(resource, request, response, 'An error has occurred while requesting to the server')
      }
    }

    return resources
  }
}
